import { defineComponent, ref, computed, watch, nextTick } from "vue/dist/vue.esm-bundler.js";

export const ChatComposer = defineComponent({
    name: "ChatComposer",
    props: {
        bridge: { type: Object, required: true },
        view: { type: Object, required: true },
        composerText: { type: String, required: true },
        submitComposer: { type: Function, required: true },
        onQuickAction: { type: Function, required: true }
    },
    emits: ["update:composerText"],
    setup(props, { emit }) {
        const suggestions = [
            { value: "/summary", desc: "要約を実行" },
            { value: "/review", desc: "動作・コードのレビュー" },
            { value: "/plan", desc: "計画・設計プランの作成" },
            { value: "/flow", desc: "フローチャートを作成" },
            { value: "/st", desc: "PLC STコードを生成" },
            { value: "/clear", desc: "チャット履歴をクリア" }
        ];

        const showSuggestions = ref(false);
        const selectedSuggestionIndex = ref(0);

        const filteredSuggestions = computed(() => {
            const text = props.composerText;
            if (!text.startsWith("/")) return [];
            const query = text.toLowerCase();
            if (query === "/") return suggestions;
            return suggestions.filter(item => item.value.toLowerCase().startsWith(query) || item.desc.toLowerCase().includes(query));
        });

        watch(() => props.composerText, (newText) => {
            if (newText.startsWith("/") && !newText.includes(" ")) {
                showSuggestions.value = filteredSuggestions.value.length > 0;
                if (selectedSuggestionIndex.value >= filteredSuggestions.value.length) {
                    selectedSuggestionIndex.value = 0;
                }
            } else {
                showSuggestions.value = false;
            }
        });

        function selectSuggestion(item) {
            emit("update:composerText", item.value + " ");
            showSuggestions.value = false;
            selectedSuggestionIndex.value = 0;
            nextTick(() => {
                const textarea = document.querySelector(".chat-input textarea");
                textarea?.focus();
            });
        }

        async function handleSubmit() {
            const text = props.composerText.trim();
            if (!text || props.view.busy) return;

            if (text.startsWith("/")) {
                const spaceIndex = text.indexOf(" ");
                const command = spaceIndex !== -1 ? text.substring(0, spaceIndex) : text;
                const payload = spaceIndex !== -1 ? text.substring(spaceIndex + 1).trim() : "";

                if (command === "/summary") {
                    await props.onQuickAction("summary");
                    emit("update:composerText", "");
                } else if (command === "/review") {
                    if (!props.view.busy) {
                        await props.bridge.runAction("review", payload);
                        emit("update:composerText", "");
                    }
                } else if (command === "/plan") {
                    if (!props.view.busy) {
                        await props.bridge.runAction("plan", payload);
                        emit("update:composerText", "");
                    }
                } else if (command === "/flow") {
                    if (!props.view.busy) {
                        await props.bridge.runAction("flow", payload);
                        emit("update:composerText", "");
                    }
                } else if (command === "/st") {
                    if (!props.view.busy) {
                        await props.bridge.runAction("st", payload);
                        emit("update:composerText", "");
                    }
                } else if (command === "/clear") {
                    await props.bridge.clearHistory();
                    emit("update:composerText", "");
                } else {
                    await props.submitComposer();
                }
            } else {
                await props.submitComposer();
            }
        }

        function handleKeyDown(e) {
            if (showSuggestions.value) {
                if (e.key === "ArrowDown") {
                    e.preventDefault();
                    selectedSuggestionIndex.value = (selectedSuggestionIndex.value + 1) % filteredSuggestions.value.length;
                } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    selectedSuggestionIndex.value = (selectedSuggestionIndex.value - 1 + filteredSuggestions.value.length) % filteredSuggestions.value.length;
                } else if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    const selected = filteredSuggestions.value[selectedSuggestionIndex.value];
                    if (selected) {
                        selectSuggestion(selected);
                    }
                } else if (e.key === "Escape") {
                    e.preventDefault();
                    showSuggestions.value = false;
                }
            } else {
                if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                }
            }
        }

        return {
            showSuggestions,
            selectedSuggestionIndex,
            filteredSuggestions,
            selectSuggestion,
            handleKeyDown,
            handleSubmit
        };
    },
    template: `
        <div class="chat-composer">
            <div v-if="showSuggestions" class="chat-composer-suggestions">
                <div
                    v-for="(item, index) in filteredSuggestions"
                    :key="item.value"
                    class="suggestion-item"
                    :class="{ 'is-selected': index === selectedSuggestionIndex }"
                    @click="selectSuggestion(item)"
                >
                    <span class="suggestion-cmd">{{ item.value }}</span>
                    <span class="suggestion-desc">{{ item.desc }}</span>
                </div>
            </div>

            <div class="chat-input">
                <textarea
                    :value="composerText"
                    :placeholder="view.inputPlaceholder"
                    @input="$emit('update:composerText', $event.target.value)"
                    @keydown="handleKeyDown"
                ></textarea>
                <button
                    type="button"
                    class="chat-send-btn"
                    @click="handleSubmit()"
                    :disabled="view.busy || !composerText.trim()"
                    title="送信"
                    aria-label="送信"
                >
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="12" y1="19" x2="12" y2="5"></line>
                        <polyline points="5 12 12 5 19 12"></polyline>
                    </svg>
                </button>
            </div>
            <div class="chat-composer-row">
                <div class="chat-composer-actions">
                    <span class="chat-clear-link" @click="bridge.clearHistory()">履歴クリア</span>
                </div>
                <div class="chat-status">{{ view.statusText }}</div>
            </div>
        </div>
    `
});
